// AuthContext.js
import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useVersionedApi } from './utils/versionedApi';
import { setTelemetryUser, clearTelemetryUser } from './config/telemetryLoader';
import { getAuthToken, safeRemoveItem } from './utils/localStorage';

import { safeStorage } from './utils/safeStorage';
import { isPlatformAdminUser } from './utils/platformAccess';
const AuthContext = createContext();

export function AuthProvider({ children }) {
  // Load cached user instantly for immediate sidebar render. The backend only
  // authenticates from the Authorization header, so a cached user without
  // evv_token is a leftover from a sign-out, not a session: never expose it,
  // not even for the first render (child effects run before the check below).
  const [user, setUser] = useState(() => {
    try {
      if (!getAuthToken()) return null;
      const cached = safeStorage.getItem('evv_user_cache');
      return cached ? JSON.parse(cached) : null;
    } catch { return null; }
  });
  const [loading, setLoading] = useState(!user); // Not loading if we have cache
  // A cached user makes `loading` false from the first render, so consumers could
  // not tell "cached, unverified" from "verified" and fired org-scoped requests
  // before /users/me had confirmed the session cookie was still alive (every one
  // 400'd when it had expired: ARASYNC-WEB-C/D). True once the startup session
  // check has finished, success or failure, and immediately when there is nothing
  // to check.
  const [authChecked, setAuthChecked] = useState(false);
  const apiClient = useVersionedApi('v1');

  // Wrapper that also caches to localStorage
  const setUserAndCache = useCallback((userData) => {
    setUser(userData);
    if (userData) {
      // Store minimal fields for session detection — full user comes from /users/me
      try { safeStorage.setItem('evv_user_cache', JSON.stringify({
        id: userData.id,
        firstName: userData.firstName,
        lastName: userData.lastName,
        role: userData.role,
        // Dashboard routing needs this before /users/me finishes on reload.
        providerType: userData.providerType ?? null,
        email: userData.email,
        organizationId: userData.organizationId,
        // A restricted login (pharmacy) is routed to its own page before /users/me returns.
        accountKind: userData.accountKind || null,
      })); } catch {}
    } else {
      try { safeStorage.removeItem('evv_user_cache'); } catch {}
    }
  }, []);

  useEffect(() => {
    const token = getAuthToken();

    // No token means signed out, whatever evv_user_cache says. The httpOnly
    // cookie never authenticates (tenantMiddleware and authMiddleware read only
    // the Authorization header), so /users/me without a token was a guaranteed
    // 400 NO_ORGANIZATION_CONTEXT, and the stale cache kept every org-scoped
    // consumer firing token-less requests. Drop the cache and send nothing.
    if (!token) {
      setUserAndCache(null);
      setLoading(false);
      setAuthChecked(true);
      return;
    }

    const fetchUser = async () => {
      // Declared OUTSIDE the try so the catch/finally can always clear the abort
      // timeout. Previously `const timeoutId` lived inside the try block, so the
      // catch's `clearTimeout(timeoutId)` threw "timeoutId is not defined",
      // masking the real auth-error handling below.
      const controller = new AbortController();
      let timeoutId;
      try {
        // Timeout to prevent a hanging auth check. Generous enough for high-latency
        // links (e.g. remote/VPN to a distant DB) where /users/me can take many
        // seconds — a too-short abort here cancels the request and used to be
        // misread as an auth failure, logging the user out mid-session.
        timeoutId = setTimeout(() => controller.abort(), 30000);

        const requestConfig = {
          signal: controller.signal,
          headers: { Authorization: `Bearer ${token}` },
        };

        // A cached platform admin (organizationId null) cannot pass
        // tenantMiddleware, so /users/me is a guaranteed 401 for them. Go
        // straight to the platform identity endpoint instead of burning a
        // round-trip and logging an error on every page load.
        let cachedIsPlatformAdmin = false;
        try {
          const cached = safeStorage.getItem('evv_user_cache');
          cachedIsPlatformAdmin = cached && isPlatformAdminUser(JSON.parse(cached));
        } catch { /* unparseable cache — fall through to /users/me */ }

        const res = await apiClient.get(
          cachedIsPlatformAdmin ? '/platform/me' : '/users/me',
          requestConfig
        );
        clearTimeout(timeoutId);

        setUserAndCache(res.data);
        void setTelemetryUser(res.data);
      } catch (error) {
        clearTimeout(timeoutId);
        const status = error.response?.status;
        const code = error.response?.data?.code;
        // 401/403 are explicit auth rejections. NO_ORGANIZATION_CONTEXT (400) is
        // returned by tenantMiddleware when there is no valid session to resolve an
        // org from — i.e. also "not authenticated". Treat it as a rejection so a
        // stale evv_user_cache doesn't keep the app in a logged-in state on the
        // login page (which fired /programs + /admin/my-sidebar, all 400ing).
        // Genuine transient failures (timeout/network/5xx) have no response body /
        // code, so they still fall through to "keep session".
        // Platform administrators (super_admin, organizationId null) cannot reach
        // /users/me at all: it is mounted after app.js's global tenantMiddleware,
        // which rejects any JWT without an organizationId as INVALID_TOKEN_PAYLOAD.
        // Without this fallback the app logged itself straight back out after a
        // successful platform login. /platform/me is mounted BEFORE that
        // middleware, so it is reachable for a tenant-less admin.
        if (status === 401 && code === 'INVALID_TOKEN_PAYLOAD') {
          try {
            const platformRes = await apiClient.get('/platform/me', {
              headers: { Authorization: `Bearer ${token}` },
            });
            setUserAndCache(platformRes.data);
            void setTelemetryUser(platformRes.data);
            return; // the finally below clears loading
          } catch (platformError) {
            // Not a platform admin either — fall through to the normal rejection
            // path below and end the session.
            console.warn('Platform identity check failed:', platformError.response?.status);
          }
        }

        const isAuthRejection = status === 401 || status === 403 || code === 'NO_ORGANIZATION_CONTEXT';
        // ONLY a real auth rejection ends the session. A canceled (our abort),
        // timed-out, or network-failed /users/me means "couldn't verify" — NOT
        // "session invalid". On a slow link these are routine; clearing the token
        // here was the cause of spurious mid-session logouts ("Auth check failed:
        // canceled"). For transient failures we keep the existing cached session.
        if (isAuthRejection) {
          console.warn('Auth check rejected — ending session:', status);
          setUserAndCache(null);
          safeRemoveItem("evv_token");
          safeStorage.removeItem('evv_user_cache');
          await clearTelemetryUser();
        } else {
          // Transient (cancel/timeout/network/5xx) — preserve the session.
          console.warn('Auth check could not complete, keeping current session:', error.code || error.name || error.message);
        }
      } finally {
        setLoading(false);
        setAuthChecked(true);
      }
    };

    fetchUser();

    // Listen for storage changes (logout in another tab)
    const handleStorageChange = (e) => {
      // Keep every open tab on the same identity. Without this, a tenant tab
      // continues its organization-scoped polling after another tab signs into
      // the tenant-less platform console, producing repeated INVALID_JWT_PAYLOAD
      // requests against notifications and visit alerts.
      if (e.key === 'evv_user_cache' && e.newValue) {
        try {
          const nextUser = JSON.parse(e.newValue);
          setUser(nextUser);
          setLoading(false);
          setAuthChecked(true);
          void setTelemetryUser(nextUser);
        } catch {
          // Ignore a malformed cross-tab cache value; the next auth check owns
          // recovery rather than replacing a valid in-memory session.
        }
        return;
      }

      // Detect logout via legacy token removal OR user cache removal (cookie auth)
      if ((e.key === 'evv_token' || e.key === 'evv_user_cache') && !e.newValue) {
        setUserAndCache(null);
        setLoading(false);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [apiClient]); // Include apiClient in dependencies

  const logout = async () => {
    // Call backend to clear httpOnly cookie and log the logout action
    try {
      await apiClient.post('/users/logout');
    } catch (err) {
      // Best-effort — proceed with client-side cleanup even if backend call fails
      console.warn('Backend logout failed:', err.message);
    }
    safeStorage.clear();
    setUserAndCache(null);
    await clearTelemetryUser();
    setLoading(false);
    window.location.href = "/";
  };

  // RBAC v2 permission helpers — read from /users/me response
  const can = useCallback((permission) => {
    if (!user) return false;
    if (user.role === 'super_admin') return true;
    // v2: check resolvedPermissions from PermissionResolverService
    if (user.resolvedPermissions) {
      return user.resolvedPermissions.includes(permission);
    }
    // v1 fallback: check legacy permissions
    const perms = [
      ...(user.permissions || []).map(p => typeof p === 'string' ? p : p.name),
      ...(user.customRole?.permissions || []).map(p => typeof p === 'string' ? p : p.name),
    ];
    return perms.includes(permission);
  }, [user]);

  const canSee = useCallback((sidebarItem) => {
    if (!user) return false;
    if (user.role === 'super_admin') return true;
    // v2: check sidebarItems from PermissionResolverService
    if (user.sidebarItems) {
      return user.sidebarItems.includes(sidebarItem);
    }
    return false;
  }, [user]);

  return (
    <AuthContext.Provider value={{ user, setUser, loading, authChecked, logout, can, canSee }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

export function hasPermission(user, permission) {
  if (!user) return false;
  
  // Super admins have access to everything
  if (user.role === 'super_admin') return true;
  
  // Check direct permissions and custom role permissions
  const perms = [
    ...(user.permissions || []).map(p => p.name),
    ...(user.customRole?.permissions || []).map(p => p.name),
  ];
  return perms.includes(permission);
}
